// Πρώτο στήσιμο κάτοψης (κατάστημα χωρίς ζώνες/τραπέζια): χώροι και πόσα τραπέζια →
// έτοιμη κάτοψη σε σειρές, που μετά διορθώνεται με σύρσιμο. Ίδιο με τη Sala.

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Circle, RectangleHorizontal, Square } from 'lucide-react';
import { boundsOf, type FloorDoc } from '../../../utils/salaFloor/floor';
import { generateFloor, type SetupArea } from '../../../utils/salaFloor/floorEdit';
import { RoomShape, VenueViewport } from './Venue';
import { Btn } from './ui';

const KINDS: { id: 'sala' | 'aylh' | 'patari' | 'taratsa'; kind: 'indoor' | 'outdoor'; start: number; counts: [number, number, number] }[] = [
  { id: 'sala', kind: 'indoor', start: 1, counts: [4, 6, 2] },
  { id: 'aylh', kind: 'outdoor', start: 21, counts: [2, 2, 1] },
  { id: 'patari', kind: 'indoor', start: 31, counts: [2, 4, 0] },
  { id: 'taratsa', kind: 'outdoor', start: 41, counts: [4, 2, 0] },
];
const SIZES = [{ icon: Circle, seats: 2 }, { icon: Square, seats: 4 }, { icon: RectangleHorizontal, seats: 6 }];

export default function FloorSetup({ storeName, onDone }: { storeName: string; onDone: (doc: FloorDoc) => void }) {
  const { t } = useTranslation(['floorEditor']);
  const k = (key: string, vars?: Record<string, unknown>) => String(t(`floorEditor:${key}`, vars as never));
  const [on, setOn] = useState<Record<string, boolean>>({ sala: true, aylh: true, patari: false, taratsa: false });
  const [counts, setCounts] = useState<Record<string, [number, number, number]>>(Object.fromEntries(KINDS.map(x => [x.id, x.counts])));
  const chosen = KINDS.filter(x => on[x.id]);
  const areas: SetupArea[] = chosen.map(x => ({ name: k(`setup.kinds.${x.id}.name`), kind: x.kind, counts: counts[x.id], start: x.start }));
  const doc = useMemo(() => generateFloor(areas), [JSON.stringify(areas)]); // eslint-disable-line react-hooks/exhaustive-deps
  const seats = doc.tables.reduce((s, x) => s + x.cap, 0);
  const setCount = (id: string, i: number, v: number) => setCounts(c => {
    const next = [...c[id]] as [number, number, number];
    next[i] = Math.max(0, Math.min(20, v));
    return { ...c, [id]: next };
  });

  return (
    <div className="flex h-full">
      <div className="flex w-full max-w-[420px] shrink-0 flex-col border-r border-line bg-surface-1">
        <div className="grid min-h-0 flex-1 auto-rows-max content-start gap-5 overflow-y-auto px-6 pb-5 pt-6">
          <div className="grid gap-1.5">
            <span className="text-[13px] text-t3">{storeName}</span>
            <h2 className="text-[22px] font-bold leading-tight text-t1">{k('setup.lead')}</h2>
            <p className="text-[14px] leading-relaxed text-t2">{k('setup.text')}</p>
          </div>
          <div className="grid gap-2">
            <h3 className="text-[14px] font-semibold text-t1">{k('setup.step1')}</h3>
            <div className="grid grid-cols-2 gap-2">
              {KINDS.map(x => {
                const yes = !!on[x.id];
                return (
                  <button key={x.id} type="button" role="checkbox" aria-checked={yes} disabled={x.id === 'sala'} onClick={() => setOn(o => ({ ...o, [x.id]: !yes }))}
                    className={`flex min-h-[52px] items-center gap-2.5 rounded-xl border-[1.5px] px-3 py-2 text-left transition-colors ${yes ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-line bg-surface-1'}`}>
                    <span className={`grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md border-2 ${yes ? 'border-blue-600 bg-blue-600 text-white' : 'border-line-strong'}`}>
                      {yes && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className="grid min-w-0 gap-px"><span className="text-[14px] font-semibold text-t1">{k(`setup.kinds.${x.id}.label`)}</span><span className="text-[12px] text-t3">{k(`setup.kinds.${x.id}.sub`)}</span></span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid gap-2">
            <h3 className="text-[14px] font-semibold text-t1">{k('setup.step2')}</h3>
            {chosen.map(x => {
              const c = counts[x.id];
              const name = k(`setup.kinds.${x.id}.name`);
              return (
                <div key={x.id} className="grid gap-1 rounded-xl border border-line bg-surface-1 px-3.5 py-3 shadow-sm">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[15.5px] font-bold text-t1">{name}</span>
                    <span className="text-[12px] text-t3">{k('common.tables', { count: c[0] + c[1] + c[2] })} · {k('common.seats', { count: c[0] * 2 + c[1] * 4 + c[2] * 6 })}</span>
                  </div>
                  {SIZES.map((s, i) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-[14px] text-t1"><s.icon className="h-4 w-4 text-t3" />{k('setup.tablesFor', { count: s.seats })}</span>
                      <div className="inline-flex h-10 items-center rounded-lg border border-line bg-surface-2">
                        <button type="button" aria-label={k('setup.less', { name })} disabled={c[i] <= 0} onClick={() => setCount(x.id, i, c[i] - 1)} className="grid h-10 w-10 place-items-center text-[18px] text-t2 disabled:opacity-30">−</button>
                        <span className="min-w-[36px] text-center text-[15px] font-bold tabular-nums text-t1">{c[i]}</span>
                        <button type="button" aria-label={k('setup.more', { name })} disabled={c[i] >= 20} onClick={() => setCount(x.id, i, c[i] + 1)} className="grid h-10 w-10 place-items-center text-[18px] text-t2 disabled:opacity-30">+</button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-3 border-t border-line px-6 py-3.5">
          <div className="grid flex-1">
            <span className="text-[18px] font-bold text-t1">{k('common.tables', { count: doc.tables.length })}</span>
            <span className="text-[12px] text-t3">{k('common.seats', { count: seats })} · {k('common.rooms', { count: chosen.length })}</span>
          </div>
          <Btn variant="primary" disabled={!doc.tables.length} onClick={() => onDone(doc)}>{k('setup.make')} <ArrowRight className="h-4 w-4" /></Btn>
        </div>
      </div>
      <VenueViewport bounds={boundsOf(doc.rooms)} className="hidden min-w-0 flex-1 bg-surface-0 md:block" pad={[30, 60, 30, 30]} maxScale={1.1}
        overlay={<span className="absolute left-5 top-4 text-[12px] font-semibold text-t3">{k('setup.preview')}</span>}>
        {doc.rooms.map(r => {
          const tl = doc.tables.filter(x => x.y > r.y && x.y < r.y + r.h);
          return (
            <RoomShape key={r.key} room={r} label={<>
              <span className="text-[17px] font-bold text-t1">{r.name}</span>
              <span className="text-[12.5px] text-t3">{k('common.tables', { count: tl.length })} · {k('common.seats', { count: tl.reduce((s, x) => s + x.cap, 0) })}</span>
            </>} />
          );
        })}
        {doc.tables.map(x => (
          <div key={x.key} className={`absolute flex flex-col items-center justify-center gap-0.5 border-2 border-line-strong bg-surface-1 shadow-sm ${x.shape === 'round' ? 'rounded-full' : 'rounded-[16px]'}`}
            style={{ left: x.x - x.w / 2, top: x.y - x.h / 2, width: x.w, height: x.h }}>
            <span className="text-[22px] font-bold leading-none text-t1">{x.n}</span>
            <span className="text-[11px] font-semibold text-t3">{k('common.seats', { count: x.cap })}</span>
          </div>
        ))}
      </VenueViewport>
    </div>
  );
}
